import { cn } from '@/utils/cn';

import { bandFor } from './bands';

/**
 * A fit score, never shown without its confidence.
 *
 * Ninety per cent from two data points is a different claim from ninety per
 * cent from a full profile, so the two travel together everywhere. At zero
 * confidence there is no number to show at all -- the badge says so rather
 * than printing a 0% that reads as "terrible fit".
 */

export function MatchBadge({ match, size = 'md', className }) {
  if (!match) return null;

  if (match.confidence === 0) {
    return (
      <span
        className={cn(
          'text-ink-muted bg-surface-muted ring-line inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ring-1',
          className,
        )}
        title="Not enough on the profile to compare"
      >
        Not enough to compare
      </span>
    );
  }

  const band = bandFor(match.score);
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1.5 rounded-full ring-1',
        size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2.5 py-0.5 text-xs',
        band.tone,
        className,
      )}
      title={`${band.label} — assessed from ${match.confidence}% of the profile`}
    >
      <span className="font-semibold">{match.score}%</span>
      <span>{band.label}</span>
    </span>
  );
}

/** The confidence, spelled out. Kept separate so a caller can place it. */
export function MatchConfidence({ match, className }) {
  if (!match) return null;
  return (
    <span className={cn('text-ink-muted text-xs', className)}>
      {match.confidence === 0
        ? 'Nothing on the profile to compare yet'
        : `Based on ${match.confidence}% of what we score`}
    </span>
  );
}
